import * as React from 'react';

import { RichText, RichTextInput } from '@/design';

/**
 * The visual editor WEB-10 asks for, and the rendering behind the preview.
 *
 * **Both are the design system's — not this domain's, and not the kit's.** This module began by
 * importing `@invent/wl-ui-kit-next`'s Tiptap editor, because when it was written nothing in
 * `src/design` had one. SR-222 built one — `src/design/rich-text-editor/`, over `@tiptap/react`
 * with the extension set survey-api's sanitiser actually accepts — and beside it the kit's is the
 * wrong choice for two reasons that are not taste:
 *
 * - **It is loaded lazily.** `RichTextInput` pulls the editor into an async chunk of its own, and
 *   the vendor chunk is initial-only (`rspack.production.js`), so an advisor who never opens an
 *   editor never downloads Tiptap. Importing the kit's editor at a module's top level, as this
 *   did, put it in the initial vendor chunk for everybody — measured at the time as +126 KB
 *   gzipped — and quietly undid that.
 * - **It agrees with the server about what may be written.** The editor's extensions and
 *   `sanitizeRichText` are the same allowlist survey-api stores by, so what an author sees saved
 *   is what survives. A second editor with its own extension set writes markup the sanitiser then
 *   drops without saying so.
 *
 * `RichText` likewise replaces the `dangerouslySetInnerHTML` this module carried. That had a
 * comment admitting it was safe only while every string reached it from this browser, and that
 * the day a content endpoint existed it would become stored XSS on a screen whose viewer is an
 * administrator. That day has come — a post's body is survey-api's now — and both halves hold:
 * the server sanitises on the way in and stores what it kept, and `RichText` sanitises again on
 * the way out, so neither side is the only thing standing between a pasted `<script>` and an
 * administrator's session.
 */
export function BodyEditor({
  label,
  value,
  onChange,
  disabled,
  onPickImage
}: {
  label: string;
  value: string;
  onChange: (next: string) => void;
  disabled?: boolean;
  /** Places a picture in the words (WEB-06 AC3). Absent where there is no library to draw from. */
  onPickImage?: () => Promise<Nullable<{ url: string; alt: string }>>;
}) {
  return (
    <RichTextInput
      label={label}
      value={value}
      onChange={onChange}
      disabled={disabled}
      onPickImage={onPickImage}
      // A post's body is survey-api's: it folded every library picture to a name on the way in
      // and answered it as a URL on the way out, so the pictures here are ones it has already
      // judged. Without this the editor opened a saved article with its images stripped — and
      // the next save wrote that back, losing them for good.
      serverResolvedImages
    />
  );
}

/**
 * Content as the site would draw it, for the preview (WEB-13 AC3) — sanitised on the way in.
 *
 * `serverResolvedImages` is set because this draws a body survey-api answered, and its rule for
 * an image is narrower than the renderer's: a stored body may hold a `data:` raster, an org
 * image or a name of the site's own media library, and a URL here was resolved from that name.
 * Without it the preview would drop exactly the pictures the author had just placed.
 */
export function RenderedBody({ html }: { html: string }) {
  if (!html.trim()) {
    return <p className="text-sm text-muted-foreground">Nothing written yet.</p>;
  }
  return <RichText html={html} serverResolvedImages />;
}
